import { useEffect, useState, useCallback } from 'react';
import {
  Settings, Phone, Mail, Shield, AlertCircle, CheckCircle, Info, Banknote,
  Clock, Loader2, X
} from 'lucide-react';
import { useLocalization } from '../contexts/LocalizationContext';
import { withdrawalService } from '../services/withdrawalService';
import LoadingScreen from './LoadingScreen';

type Operator = 'airtel' | 'mpesa' | 'orange' | 'afrimoney';

interface ConfigShape {
  configured: boolean;
  email: string;
  phone_airtel: string | null;
  phone_mpesa: string | null;
  phone_orange: string | null;
  phone_afrimoney: string | null;
  daily_limit_cdf?: number;
  daily_limit_usd?: number;
  pending_request?: {
    id: number;
    requested_at: string;
    requested_values: Record<string, string | null>;
    diff_summary: Array<{ field: string; from: string | null; to: string | null }>;
  } | null;
  last_review?: {
    status: 'approved' | 'rejected';
    reviewed_at: string | null;
    rejection_reason?: string | null;
  } | null;
}

const OPERATORS: { key: `phone_${Operator}`; label: string; placeholder: string; color: string }[] = [
  { key: 'phone_mpesa',     label: 'M-Pesa (Vodacom)', placeholder: '0828XXXXXXX', color: 'text-blue-500' },
  { key: 'phone_airtel',    label: 'Airtel Money',     placeholder: '0997XXXXXXX', color: 'text-red-500' },
  { key: 'phone_orange',    label: 'Orange Money',     placeholder: '0858XXXXXXX', color: 'text-orange-500' },
  { key: 'phone_afrimoney', label: 'Afrimoney',        placeholder: '0901XXXXXXX', color: 'text-purple-500' },
];

export default function CashoutSettings() {
  const { t } = useLocalization();

  const [config, setConfig] = useState<ConfigShape | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    email: '', phone_mpesa: '', phone_airtel: '', phone_orange: '', phone_afrimoney: '',
  });
  const [message, setMessage] = useState<{ type: 'success' | 'info' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await withdrawalService.getWithdrawalConfig();
      setConfig(data);
      setForm({
        email: data?.email || '',
        phone_mpesa: data?.phone_mpesa || '',
        phone_airtel: data?.phone_airtel || '',
        phone_orange: data?.phone_orange || '',
        phone_afrimoney: data?.phone_afrimoney || '',
      });
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-clear success/info messages after 6s.
  useEffect(() => {
    if (!message || message.type === 'error') return;
    const id = setTimeout(() => setMessage(null), 6000);
    return () => clearTimeout(id);
  }, [message]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await withdrawalService.updateWithdrawalConfig(form);
      if (res?.no_change) {
        setMessage({ type: 'info', text: t('cashoutSettings.noChange') || 'Aucun champ modifié.' });
      } else {
        setMessage({
          type: 'success',
          text: t('cashoutSettings.submitted') || 'Demande envoyée pour approbation backoffice.',
        });
      }
      await load();
      setEditing(false);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === 'pending_request_exists') {
        setMessage({
          type: 'info',
          text: t('cashoutSettings.pendingExists') || "Une demande est déjà en attente d'approbation.",
        });
        await load();
        setEditing(false);
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || e?.message || 'Echec de la soumission');
        setMessage({ type: 'error', text: msg });
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelPending = async () => {
    try {
      await withdrawalService.cancelPendingWithdrawalConfig();
      setMessage({ type: 'info', text: t('cashoutSettings.pendingCancelled') || 'Demande annulée.' });
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Echec de l\'annulation';
      setMessage({ type: 'error', text: msg });
    }
  };

  if (loading) return <LoadingScreen fullscreen={false} message={t('cashoutSettings.loading') || 'Chargement...'} />;

  const isConfigured = !!config?.configured;
  const pending = config?.pending_request || null;
  const lastReview = config?.last_review || null;
  const configuredCount = OPERATORS.filter((op) => (config as any)?.[op.key]).length;
  const editingDisabled = !!pending;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 text-white">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="absolute -top-32 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-teal-500/15 rounded-full blur-[100px]" />

        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl">
              <Banknote className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">{t('cashoutSettings.title') || 'Paramètres de décaissement'}</h1>
              <p className="text-slate-400 mt-1 text-sm">
                {t('cashoutSettings.subtitle') || 'Email OTP et numéros mobile money pour vos décaissements (mobile money & cartes).'}
              </p>
            </div>
          </div>

          <div className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 ${isConfigured ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-amber-500/10 border border-amber-500/20 text-amber-400'}`}>
            {isConfigured ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {isConfigured ? (t('cashoutSettings.statusConfigured') || 'Configuré') : (t('cashoutSettings.statusNotConfigured') || 'Non configuré')}
          </div>
        </div>
      </div>

      {/* Toast / message */}
      {message && (
        <div className={`p-4 rounded-xl text-sm flex items-start justify-between gap-3 border ${
          message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
          message.type === 'info'    ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                       'bg-rose-50 text-rose-700 border-rose-200'
        }`}>
          <div className="flex items-start gap-2">
            {message.type === 'success' ? <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" /> :
             message.type === 'info'    ? <Info className="w-4 h-4 mt-0.5 shrink-0" /> :
                                          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="opacity-60 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Pending banner */}
      {pending && (
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-900">
                {t('cashoutSettings.pendingTitle') || "Demande en attente d'approbation"}
              </h3>
              <p className="text-xs text-amber-800 mt-1">
                {t('cashoutSettings.pendingDesc') || "Le backoffice doit approuver vos changements avant qu'ils ne soient appliqués. Cela peut prendre quelques minutes à quelques heures."}
              </p>
              {pending.diff_summary && pending.diff_summary.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-amber-900">
                  {pending.diff_summary.map((d, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <span className="font-medium">{labelForField(d.field, t)}</span>
                      <span className="line-through opacity-60">{d.from || '—'}</span>
                      <span>→</span>
                      <span className="font-semibold">{d.to || '—'}</span>
                    </li>
                  ))}
                </ul>
              )}
              <button
                onClick={cancelPending}
                className="mt-3 text-xs font-medium text-amber-700 hover:underline"
              >
                {t('cashoutSettings.cancelPending') || 'Annuler la demande'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Last rejection banner */}
      {lastReview?.status === 'rejected' && !pending && (
        <div className="p-4 rounded-xl bg-rose-50 border border-rose-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-rose-900">
                {t('cashoutSettings.rejectedTitle') || 'Dernière demande rejetée'}
              </h3>
              {lastReview.rejection_reason && (
                <p className="text-xs text-rose-800 mt-1">{lastReview.rejection_reason}</p>
              )}
              <p className="text-xs text-rose-700 mt-2">
                {t('cashoutSettings.rejectedAdvice') || 'Corrigez les valeurs ci-dessous puis soumettez à nouveau.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: form (2/3) */}
        <div className="lg:col-span-2 space-y-5">
          {/* Approval notice */}
          <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 flex items-start gap-2">
            <Shield className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-xs text-blue-800">
              {t('cashoutSettings.approvalNotice') || "Toute modification (email OTP ou numéros) est soumise à validation par notre backoffice avant d'être appliquée."}
            </p>
          </div>

          {/* Email */}
          <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-5 h-5 text-blue-500" />
              <h3 className="text-base font-semibold text-gray-900">{t('cashoutSettings.emailTitle') || 'Email pour vérification OTP'}</h3>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              {t('cashoutSettings.emailDesc') || 'Un code OTP à 6 chiffres sera envoyé à cet email pour chaque décaissement.'}
            </p>
            {editing ? (
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="your@email.com"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              />
            ) : (
              <div className="text-sm text-gray-900 font-medium">
                {config?.email ? config.email : <span className="text-gray-400 italic">{t('cashoutSettings.notSet') || 'Non renseigné'}</span>}
              </div>
            )}
          </div>

          {/* Phone numbers */}
          <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Phone className="w-5 h-5 text-emerald-500" />
              <h3 className="text-base font-semibold text-gray-900">{t('cashoutSettings.phonesTitle') || 'Numéros par opérateur'}</h3>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              {t('cashoutSettings.phonesDesc') || 'Un seul numéro par opérateur. Tous les décaissements (mobile money et cartes) atterrissent ici.'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {OPERATORS.map((op) => (
                <div key={op.key}>
                  <label className={`block text-sm font-medium mb-1.5 ${op.color}`}>{op.label}</label>
                  {editing ? (
                    <input
                      type="text"
                      value={(form as any)[op.key] || ''}
                      onChange={(e) => setForm((p) => ({ ...p, [op.key]: e.target.value }))}
                      placeholder={op.placeholder}
                      className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                  ) : (
                    <div className="px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800">
                      {(config as any)?.[op.key] || <span className="text-gray-400 italic">{t('cashoutSettings.notSet') || 'Non renseigné'}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Action row */}
          <div className="flex items-center gap-3">
            {editing ? (
              <>
                <button
                  onClick={() => { setEditing(false); setMessage(null); load(); }}
                  className="px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
                >
                  {t('common.cancel') || 'Annuler'}
                </button>
                <button
                  onClick={save}
                  disabled={saving || !form.email}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-sm font-semibold hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50"
                >
                  {saving
                    ? (<><Loader2 className="w-4 h-4 animate-spin" /> {t('cashoutSettings.submitting') || 'Envoi...'}</>)
                    : (<><Settings className="w-4 h-4" /> {t('cashoutSettings.submitForReview') || 'Soumettre pour validation'}</>)}
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditing(true)}
                disabled={editingDisabled}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-sm font-semibold hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed"
                title={editingDisabled ? (t('cashoutSettings.disabledPending') || 'Modifier indisponible : une demande est en attente') : undefined}
              >
                <Settings className="w-4 h-4" />
                {isConfigured
                  ? (t('cashoutSettings.modify') || 'Modifier la configuration')
                  : (t('cashoutSettings.configure') || 'Configurer')}
              </button>
            )}
          </div>
        </div>

        {/* Right: side panel (1/3) */}
        <div className="space-y-5">
          {/* Coverage */}
          <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('cashoutSettings.coverageTitle') || 'Couverture opérateurs'}</h3>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-2xl font-bold text-gray-900">{configuredCount}</span>
              <span className="text-sm text-gray-400">/ {OPERATORS.length}</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(configuredCount / OPERATORS.length) * 100}%` }} />
            </div>
            <ul className="mt-3 space-y-1.5 text-xs">
              {OPERATORS.map((op) => {
                const set = !!(config as any)?.[op.key];
                return (
                  <li key={op.key} className="flex items-center justify-between">
                    <span className={op.color}>{op.label}</span>
                    {set ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle className="w-3 h-3" /> {t('cashoutSettings.covered') || 'Configuré'}</span>
                    ) : (
                      <span className="text-gray-400">{t('cashoutSettings.empty') || 'Vide'}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Security */}
          <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-indigo-500" />
              <h3 className="text-sm font-semibold text-gray-900">{t('cashoutSettings.securityTitle') || 'Sécurité'}</h3>
            </div>
            <ul className="space-y-2.5 text-xs text-gray-600">
              <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> {t('cashoutSettings.secOTP') || 'OTP requis pour chaque décaissement'}</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> {t('cashoutSettings.secApproval') || 'Tout changement passe par notre backoffice'}</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> {t('cashoutSettings.secNumbersOnly') || 'Décaissements uniquement vers vos numéros enregistrés'}</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> {t('cashoutSettings.secLimits') || 'Limites quotidiennes appliquées automatiquement'}</li>
            </ul>
          </div>

          {/* Daily limits (if backend provides them) */}
          {(config?.daily_limit_cdf != null || config?.daily_limit_usd != null) && (
            <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Info className="w-5 h-5 text-amber-500" />
                <h3 className="text-sm font-semibold text-gray-900">{t('cashoutSettings.limitsTitle') || 'Limites quotidiennes'}</h3>
              </div>
              <div className="space-y-2.5">
                {config.daily_limit_cdf != null && (
                  <div className="flex justify-between items-center p-2.5 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">CDF</span>
                    <span className="text-sm font-bold text-gray-900">{Number(config.daily_limit_cdf).toLocaleString()}</span>
                  </div>
                )}
                {config.daily_limit_usd != null && (
                  <div className="flex justify-between items-center p-2.5 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">USD</span>
                    <span className="text-sm font-bold text-gray-900">{Number(config.daily_limit_usd).toLocaleString()}</span>
                  </div>
                )}
              </div>
              <p className="text-[10px] text-gray-400 mt-3">{t('cashoutSettings.limitsHelp') || 'Contactez le support pour ajuster vos limites.'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function labelForField(field: string, t: (k: string) => string): string {
  const map: Record<string, string> = {
    email: t('cashoutSettings.fieldEmail') || 'Email OTP',
    phone_airtel: 'Airtel',
    phone_mpesa: 'M-Pesa',
    phone_orange: 'Orange',
    phone_afrimoney: 'Afrimoney',
  };
  return map[field] || field;
}
